import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
/* -----------------UI--------------- */
import Box from "@memorang/ui/components/Box";
import Button from "@memorang/ui/components/Button";
import DonutScore from "@memorang/ui/components/DonutScore";
import type { IconType } from "@memorang/ui/icon";
import { Pressable } from "react-native";
import { Card, Chip, Divider, Text } from "react-native-paper";
import type { EXAM_STATUS } from "types/tag";
import { buttonConfig } from "../constants";
import { getExamStatusIcon } from "../helpers/practice-tests-data";
import CardHeaderAvatar from "./CardHeaderAvatar";

type Props = {
	label: string;
	description: string;
	text: string;
	icon: string;
	status: string;
	cta: string;
	handleNavigation: () => void;
	loading?: boolean;
	score: number;
};

export const PracticeTestListItem = ({
	label: title,
	description,
	text: label,
	icon,
	status,
	cta,
	handleNavigation,
	loading,
	score,
}: Props) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();
	const scoreRanges = useAppStore((state) => state.app?.scoreRanges);

	const { buttonMode, buttonIcon } = buttonConfig[status];
	const examStatusIcon = getExamStatusIcon(status as unknown as EXAM_STATUS);

	const Left = () => (
		<Box style={{ justifyContent: "center" }}>
			{icon ? (
				<CardHeaderAvatar icon={icon} />
			) : (
				<Box width={40} height={40} alignSelf="center">
					<DonutScore
						scoreRanges={scoreRanges}
						distribution={[score, 100 - score]}
						score={score}
						variant="progress"
						radius={20}
						innerRadius={16}
					/>
				</Box>
			)}
		</Box>
	);

	if (isMobile) {
		return (
			<>
				<Pressable onPress={handleNavigation} disabled={loading}>
					<Box
						paddingVertical={12}
						paddingHorizontal={16}
						flexDirection="row"
						alignItems="center"
						gap={12}
					>
						<Left />
						<Box flex={1} gap={4}>
							<Text variant="titleMedium">{title}</Text>
							<Text
								variant="bodySmall"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{description}
							</Text>
							<Box flexDirection="row" gap={8} paddingTop={4}>
								<Chip
									compact
									icon={(props) => (
										<Icon {...props} name={examStatusIcon as IconType} />
									)}
								>
									{label}
								</Chip>
							</Box>
						</Box>
					</Box>
					<Box paddingHorizontal={16} paddingBottom={8} alignItems="flex-end">
						<Button
							mode={buttonMode}
							icon={buttonIcon}
							loading={loading}
							disabled={loading}
							onPress={handleNavigation}
							compact
						>
							{cta}
						</Button>
					</Box>
				</Pressable>
				<Divider />
			</>
		);
	}

	return (
		<Box>
			<Card
				mode="elevated"
				onPress={handleNavigation}
				style={{
					overflow: "hidden",
					paddingVertical: 16,
					marginHorizontal: 16,
				}}
			>
				<Card.Title
					title={title}
					subtitleNumberOfLines={2}
					subtitle={
						<Box style={{ gap: 8 }}>
							<Text variant="bodySmall">{description}</Text>
							<Box flexDirection="row" gap={8}>
								<Chip
									compact
									icon={(props) => (
										<Icon {...props} name={examStatusIcon as IconType} />
									)}
								>
									{label}
								</Chip>
							</Box>
						</Box>
					}
					left={() => (
						<Box style={{ marginRight: 8, justifyContent: "center" }}>
							<Left />
						</Box>
					)}
					right={() => (
						<Box justifyContent="center" paddingRight={16}>
							<Button
								mode={buttonMode}
								icon={buttonIcon}
								loading={loading}
								disabled={loading}
								onPress={handleNavigation}
							>
								{cta}
							</Button>
						</Box>
					)}
				/>
			</Card>
		</Box>
	);
};
